import { createContext, useContext, useEffect, useState } from 'react'

/**
 * The hovered timeline element, kept as view state (not store state — it never
 * needs to persist or survive a re-mount). `x`/`y` are client coordinates, used
 * to position `GanttTooltip`. Lives in its own module, mirroring `gantt-layout.ts`,
 * so the timeline layers can read it without a circular import through `GanttView`.
 */
export type HoverState =
  | null
  | { kind: 'activity'; id: string; x: number; y: number }
  | { kind: 'link'; id: string; x: number; y: number }
  | { kind: 'ghost'; id: string; x: number; y: number }

/**
 * Split into a value and a dispatch context. `GanttTimeline` (and `DependencyLayer`)
 * only ever *write* the hover — they draw every bar/link/ghost, so if they also
 * subscribed to the value they would re-render on every pointermove. `GanttTooltip`
 * is the sole reader and subscribes to the value context instead. `setHover` from
 * `useState` is referentially stable across renders, so the dispatch context's
 * value never changes identity and never forces a re-render of its own.
 */
export const GanttHoverStateContext = createContext<HoverState>(null)
export const GanttHoverDispatchContext = createContext<(hover: HoverState) => void>(() => {})

/** A tiny external store mirroring the same hover value the state Context carries, kept alongside
 * it purely so `useHoveredActivityId` (Task 18) can subscribe with per-id precision — a plain
 * Context read re-renders every consumer on any hover change, which is exactly the "hovered row"
 * fan-out the Task 10 review eliminated from `GanttTimeline`/`DependencyLayer`. `GanttHoverProvider`
 * owns one instance and updates it from the same `setHover` call that updates the state Context. */
export interface HoverStore {
  get: () => HoverState
  set: (h: HoverState) => void
  subscribe: (listener: () => void) => () => void
}

export const createHoverStore = (): HoverStore => {
  let state: HoverState = null
  const listeners = new Set<() => void>()
  return {
    get: () => state,
    set: (h) => { state = h; listeners.forEach((l) => l()) },
    subscribe: (l) => { listeners.add(l); return () => { listeners.delete(l) } },
  }
}

export const GanttHoverStoreContext = createContext<HoverStore | null>(null)

/**
 * Per-activity "is this bar hovered" flag for Task 18's resize/link handles
 * (`showHandles = canEdit && (hovered || selected)`). Subscribes to `GanttHoverStoreContext`
 * directly (rather than reading `GanttHoverStateContext`'s value via `useContext`, which would
 * re-render on *every* hover change regardless of whether this particular id's flag differs — with
 * N rows, that means all N bars re-running on every pointer move, the exact fan-out the Task 10
 * review eliminated). `setHovered` only receives a genuinely new boolean when this id's hovered
 * state actually flips, and React already bails out of re-rendering a component whose state setter
 * is called with the same primitive value — so, in effect, at most the newly- and
 * previously-hovered rows re-render.
 *
 * (`useSyncExternalStore` would be the idiomatic React 18 tool for this, but the pinned
 * `@types/react@17.0.3` doesn't declare it — this plain subscribe/`useEffect` gets the same
 * per-id-precise result.)
 */
export const useHoveredActivityId = (activityId: string): boolean => {
  const store = useContext(GanttHoverStoreContext)
  const [hovered, setHovered] = useState(() => {
    const h = store ? store.get() : null
    return !!h && h.kind === 'activity' && h.id === activityId
  })
  useEffect(() => {
    if (!store) return undefined
    const check = () => {
      const h = store.get()
      setHovered(!!h && h.kind === 'activity' && h.id === activityId)
    }
    check() // the id itself may have changed since the last render
    return store.subscribe(check)
  }, [store, activityId])
  return hovered
}
